import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Edit, 
  QrCode, 
  UserPlus, 
  UserMinus,
  Package,
  MapPin,
  User,
  DollarSign,
  Clock,
  History,
  Trash2,
  ArrowRightLeft,
  Wrench,
  Download,
  AlertTriangle,
  X,
  Search,
  Check,
  MessageSquareWarning
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, formatDate, formatDateTime, formatCurrency, getStatusColor } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';
import ReportIssueDialog from '@/components/ReportIssueDialog';
import { PermissionGate } from '@/components/PermissionGate';

interface HistoryEntry {
  id: string;
  action: string;
  performedAt: string | number;
  performedBy?: string;
  performedByUser?: { firstName: string; lastName: string } | null;
  performedByName?: string;
  notes?: string;
  oldStatus?: string;
  newStatus?: string;
  oldEmployeeId?: string;
  newEmployeeId?: string;
  oldEmployee?: { id: string; firstName: string; lastName: string; employeeId?: string } | null;
  newEmployee?: { id: string; firstName: string; lastName: string; employeeId?: string } | null;
  oldBranchId?: string;
  newBranchId?: string;
  oldBranch?: { id: string; name: string } | null;
  newBranch?: { id: string; name: string } | null;
}

interface MaintenanceRecord {
  id: string;
  type: string;
  description: string;
  cost?: string;
  startDate: string;
  endDate?: string;
  performedBy?: { firstName: string; lastName: string };
  status: string;
}

interface TransferRecord {
  id: string;
  fromUser?: { id: string; firstName: string; lastName: string };
  toUser?: { id: string; firstName: string; lastName: string };
  fromBranch?: { id: string; name: string };
  toBranch?: { id: string; name: string };
  fromDepartment?: { id: string; name: string };
  toDepartment?: { id: string; name: string };
  transferDate: string;
  reason?: string;
  transferredBy?: { firstName: string; lastName: string };
}

type AssignmentType = 'employee' | 'department' | 'branch';

export default function AssetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Check URL for action parameter
  const searchParams = new URLSearchParams(window.location.search);
  const actionParam = searchParams.get('action');

  // Modal states - auto-open based on URL action
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(actionParam === 'assign');
  const [showTransferModal, setShowTransferModal] = useState(actionParam === 'transfer');
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showRepairModal, setShowRepairModal] = useState(actionParam === 'repair');
  const [showQRModal, setShowQRModal] = useState(false);
  const [showReportIssueModal, setShowReportIssueModal] = useState(actionParam === 'issues');
  const [activeTab, setActiveTab] = useState<'history' | 'maintenance' | 'transfers'>('history');

  // Search states for dropdowns
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [assignEmployeeSearch, setAssignEmployeeSearch] = useState('');

  // Form states
  const [assignType, setAssignType] = useState<AssignmentType>('employee');
  const [assignUserId, setAssignUserId] = useState('');
  const [assignDepartmentId, setAssignDepartmentId] = useState('');
  const [assignBranchId, setAssignBranchId] = useState('');
  const [transferData, setTransferData] = useState({
    transferType: 'branch' as AssignmentType,
    toEmployeeId: '',
    toDepartmentId: '',
    toBranchId: '',
    reason: '',
  });
  const [repairData, setRepairData] = useState({
    serviceProviderId: '',
    repairType: 'CORRECTIVE',
    priority: 'MEDIUM',
    issueDescription: '',
    estimatedCost: '',
    estimatedDays: '',
    assignLoaner: false,
    loanerAssetId: '',
  });
  const [loanerSearch, setLoanerSearch] = useState('');
  const [maintenanceData, setMaintenanceData] = useState({
    type: 'PREVENTIVE',
    description: '',
    cost: '',
    startDate: new Date().toISOString().split('T')[0],
  });

  // Fetch asset
  const { data: asset, isLoading, error } = useQuery({
    queryKey: ['asset', id],
    queryFn: async () => {
      const response = await api.get(`/assets/${id}`);
      return response.data.data;
    },
    retry: 1,
  });

  // Fetch assignment history
  const { data: assignmentHistory } = useQuery({
    queryKey: ['asset-history', id],
    queryFn: async () => {
      const response = await api.get(`/assets/${id}/history`);
      return response.data.data || [];
    },
    enabled: !!id,
  });

  // Fetch maintenance records
  const { data: maintenanceRecords } = useQuery({
    queryKey: ['asset-maintenance', id],
    queryFn: async () => {
      const response = await api.get(`/assets/${id}/maintenance`);
      return response.data.data || [];
    },
    enabled: !!id,
  });

  // Fetch transfer history
  const { data: transferHistory } = useQuery({
    queryKey: ['asset-transfers', id],
    queryFn: async () => {
      const response = await api.get(`/assets/${id}/transfers`);
      return response.data.data || [];
    },
    enabled: !!id,
  });

  // Fetch employees for assignment
  const { data: employees } = useQuery({
    queryKey: ['employees-for-transfer'],
    queryFn: async () => {
      const response = await api.get('/employees?limit=500&status=ACTIVE');
      return response.data.data || [];
    },
    enabled: showAssignModal || showTransferModal,
  });

  // Fetch departments for assignment
  const { data: departments } = useQuery({
    queryKey: ['departments-for-transfer'],
    queryFn: async () => {
      const response = await api.get('/departments?limit=100');
      return response.data.data || [];
    },
    enabled: showAssignModal || showTransferModal,
  });

  // Fetch branches for transfer
  const { data: branches } = useQuery({
    queryKey: ['branches-for-transfer'],
    queryFn: async () => {
      const response = await api.get('/branches?limit=100');
      return response.data.data || [];
    },
    enabled: showAssignModal || showTransferModal,
  });

  // Fetch service providers for repair
  const { data: serviceProviders } = useQuery({
    queryKey: ['service-providers-for-repair'],
    queryFn: async () => {
      const response = await api.get('/service-providers?limit=100');
      return response.data.data || [];
    },
    enabled: showRepairModal,
  });

  // Fetch available assets for loaner (same category, AVAILABLE status)
  const { data: availableLoanerAssets } = useQuery({
    queryKey: ['available-loaner-assets', asset?.category?.id],
    queryFn: async () => {
      const response = await api.get(`/assets?status=AVAILABLE&categoryId=${asset?.category?.id}&limit=100`);
      return response.data.data || [];
    },
    enabled: showRepairModal && repairData.assignLoaner && !!asset?.category?.id,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/assets/${id}`);
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Asset deleted successfully',
      });
      navigate('/assets');
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete asset',
        variant: 'destructive',
      });
    },
  });

  // Assign mutation
  const assignMutation = useMutation({
    mutationFn: async (data: { employeeId?: string; departmentId?: string; branchId?: string }) => {
      await api.post(`/assets/${id}/assign`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
      queryClient.invalidateQueries({ queryKey: ['asset-history', id] });
      queryClient.invalidateQueries({ queryKey: ['asset-statistics'] });
      setShowAssignModal(false);
      setAssignUserId('');
      setAssignDepartmentId('');
      setAssignBranchId('');
      setAssignType('employee');
      toast({
        title: 'Success',
        description: 'Asset assigned successfully',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to assign asset',
        variant: 'destructive',
      });
    },
  });

  // Unassign mutation
  const unassignMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/assets/${id}/unassign`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
      queryClient.invalidateQueries({ queryKey: ['asset-history', id] });
      queryClient.invalidateQueries({ queryKey: ['asset-statistics'] });
      toast({
        title: 'Success',
        description: 'Asset unassigned successfully',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to unassign asset',
        variant: 'destructive',
      });
    },
  });

  // Transfer mutation
  const transferMutation = useMutation({
    mutationFn: async (data: { toEmployeeId?: string; toDepartmentId?: string; toBranchId?: string; reason?: string }) => {
      await api.post(`/assets/${id}/transfer`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
      queryClient.invalidateQueries({ queryKey: ['asset-transfers', id] });
      setShowTransferModal(false);
      setTransferData({
        transferType: 'branch',
        toEmployeeId: '',
        toDepartmentId: '',
        toBranchId: '',
        reason: '',
      });
      toast({
        title: 'Success',
        description: 'Asset transferred successfully',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to transfer asset',
        variant: 'destructive',
      });
    },
  });

  // Add maintenance mutation
  const addMaintenanceMutation = useMutation({
    mutationFn: async (data: typeof maintenanceData) => {
      await api.post(`/assets/${id}/maintenance`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
      queryClient.invalidateQueries({ queryKey: ['asset-maintenance', id] });
      setShowMaintenanceModal(false);
      setMaintenanceData({
        type: 'PREVENTIVE',
        description: '',
        cost: '',
        startDate: new Date().toISOString().split('T')[0],
      });
      toast({
        title: 'Success',
        description: 'Maintenance record added successfully',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to add maintenance record',
        variant: 'destructive',
      });
    },
  });

  // Send for repair mutation
  const sendForRepairMutation = useMutation({
    mutationFn: async (data: typeof repairData) => {
      const payload = {
        assetId: id,
        employeeId: asset?.assignedTo?.id || null,
        serviceProviderId: data.serviceProviderId || null,
        repairType: data.repairType,
        priority: data.priority,
        issueDescription: data.issueDescription,
        estimatedCost: data.estimatedCost ? parseFloat(data.estimatedCost) : null,
        estimatedDays: data.estimatedDays ? parseInt(data.estimatedDays) : null,
      };
      const response = await api.post('/repairs', payload);
      
      // If loaner is assigned, call the assign-loaner endpoint
      if (data.assignLoaner && data.loanerAssetId && response.data.data?.id) {
        await api.post(`/repairs/${response.data.data.id}/assign-loaner`, {
          loanerAssetId: data.loanerAssetId,
        });
      }
      
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
      queryClient.invalidateQueries({ queryKey: ['repairs'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['asset-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['sanalam-dashboard'] });
      setShowRepairModal(false);
      setRepairData({
        serviceProviderId: '',
        repairType: 'CORRECTIVE',
        priority: 'MEDIUM',
        issueDescription: '',
        estimatedCost: '',
        estimatedDays: '',
        assignLoaner: false,
        loanerAssetId: '',
      });
      setLoanerSearch('');
      toast({
        title: 'Success',
        description: repairData.assignLoaner 
          ? 'Asset sent for repair and loaner assigned successfully'
          : 'Asset sent for repair successfully',
      });
      navigate('/repairs');
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to send asset for repair',
        variant: 'destructive',
      });
    },
  });

  // Generate QR Code
  const generateQRCode = () => {
    const qrData = JSON.stringify({
      id: asset.id,
      assetTag: asset.assetTag,
      name: asset.name,
      serialNumber: asset.serialNumber,
    });
    // Open in new window with QR code (using a simple QR API)
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData)}`;
    window.open(qrUrl, '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Error loading asset</p>
        <p className="text-muted-foreground text-sm mt-2">{(error as Error).message}</p>
        <Button asChild className="mt-4">
          <Link to="/assets">Back to Assets</Link>
        </Button>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Asset not found</p>
        <Button asChild className="mt-4">
          <Link to="/assets">Back to Assets</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                </div>
                <h3 className="text-lg font-semibold">Delete Asset</h3>
              </div>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete <strong>{asset.name}</strong> ({asset.assetTag})? 
                This action cannot be undone and will remove all associated records.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => deleteMutation.mutate()}
                  loading={deleteMutation.isPending}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Asset
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Assign Asset</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowAssignModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Assignment Info */}
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm font-medium mb-1">Current Assignment</p>
                <p className="text-sm text-muted-foreground">
                  {asset.assignedTo ? (
                    <>Employee: {asset.assignedTo.firstName} {asset.assignedTo.lastName}</>
                  ) : asset.department ? (
                    <>Department: {asset.department.name}</>
                  ) : asset.branch ? (
                    <>Branch: {asset.branch.name}</>
                  ) : (
                    'Not assigned'
                  )}
                </p>
              </div>

              {/* Assign To Type Selection */}
              <div>
                <Label>Assign To</Label>
                <select
                  className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                  value={assignType}
                  onChange={(e) => {
                    setAssignType(e.target.value as AssignmentType);
                    setAssignUserId('');
                    setAssignDepartmentId('');
                    setAssignBranchId('');
                  }}
                >
                  <option value="employee">Employee</option>
                  <option value="department">Department</option>
                  <option value="branch">Branch</option>
                </select>
              </div>

              {/* Dynamic Selection Based on Type */}
              {assignType === 'employee' && (
                <div>
                  <Label>Select Employee</Label>
                  {/* Search Input */}
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search employees by name or email..."
                      className="pl-10"
                      value={assignEmployeeSearch}
                      onChange={(e) => setAssignEmployeeSearch(e.target.value)}
                    />
                  </div>
                  {/* Employee List */}
                  <div className="mt-2 max-h-48 overflow-y-auto border rounded-md">
                    {(employees || [])
                      .filter((emp: any) => {
                        if (!assignEmployeeSearch) return true;
                        const search = assignEmployeeSearch.toLowerCase();
                        return (
                          emp.firstName?.toLowerCase().includes(search) ||
                          emp.lastName?.toLowerCase().includes(search) ||
                          emp.email?.toLowerCase().includes(search) ||
                          emp.employeeId?.toLowerCase().includes(search)
                        );
                      })
                      .slice(0, 50)
                      .map((emp: any) => (
                        <div
                          key={emp.id}
                          className={cn(
                            "px-3 py-2 cursor-pointer hover:bg-muted flex items-center justify-between",
                            assignUserId === emp.id && "bg-primary/10"
                          )}
                          onClick={() => setAssignUserId(emp.id)}
                        >
                          <div>
                            <p className="text-sm font-medium">{emp.firstName} {emp.lastName}</p>
                            <p className="text-xs text-muted-foreground">{emp.email || emp.employeeId}</p>
                          </div>
                          {assignUserId === emp.id && (
                            <Check className="w-4 h-4 text-primary" />
                          )}
                        </div>
                      ))}
                    {(employees || []).filter((emp: any) => {
                      if (!assignEmployeeSearch) return true;
                      const search = assignEmployeeSearch.toLowerCase();
                      return (
                        emp.firstName?.toLowerCase().includes(search) ||
                        emp.lastName?.toLowerCase().includes(search) ||
                        emp.email?.toLowerCase().includes(search)
                      );
                    }).length === 0 && (
                      <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                        No employees found
                      </p>
                    )}
                  </div>
                  {assignUserId && (
                    <p className="mt-2 text-sm text-primary">
                      Selected: {(employees || []).find((e: any) => e.id === assignUserId)?.firstName} {(employees || []).find((e: any) => e.id === assignUserId)?.lastName}
                    </p>
                  )}
                </div>
              )}

              {assignType === 'department' && (
                <div>
                  <Label>Select Department</Label>
                  <select
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    value={assignDepartmentId}
                    onChange={(e) => setAssignDepartmentId(e.target.value)}
                  >
                    <option value="">Select a department...</option>
                    {(departments || []).map((dept: any) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {assignType === 'branch' && (
                <div>
                  <Label>Select Branch</Label>
                  <select
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    value={assignBranchId}
                    onChange={(e) => setAssignBranchId(e.target.value)}
                  >
                    <option value="">Select a branch...</option>
                    {(branches || []).map((branch: any) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAssignModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    const assignData: { employeeId?: string; departmentId?: string; branchId?: string } = {};
                    if (assignType === 'employee') assignData.employeeId = assignUserId;
                    if (assignType === 'department') assignData.departmentId = assignDepartmentId;
                    if (assignType === 'branch') assignData.branchId = assignBranchId;
                    assignMutation.mutate(assignData);
                  }}
                  disabled={
                    (assignType === 'employee' && !assignUserId) ||
                    (assignType === 'department' && !assignDepartmentId) ||
                    (assignType === 'branch' && !assignBranchId)
                  }
                  loading={assignMutation.isPending}
                >
                  Assign
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Transfer Asset</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowTransferModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Assignment Info */}
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm font-medium mb-1">Current Assignment</p>
                <div className="text-sm text-muted-foreground space-y-1">
                  {asset.assignedTo && (
                    <p>Employee: {asset.assignedTo.firstName} {asset.assignedTo.lastName}</p>
                  )}
                  {asset.department && (
                    <p>Department: {asset.department.name}</p>
                  )}
                  {asset.branch && (
                    <p>Branch: {asset.branch.name}</p>
                  )}
                  {!asset.assignedTo && !asset.department && !asset.branch && (
                    <p>Not assigned</p>
                  )}
                </div>
              </div>

              {/* Transfer Type Selection */}
              <div>
                <Label>Transfer Type</Label>
                <select
                  className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                  value={transferData.transferType}
                  onChange={(e) => setTransferData({
                    ...transferData,
                    transferType: e.target.value as AssignmentType,
                    toEmployeeId: '',
                    toDepartmentId: '',
                    toBranchId: '',
                  })}
                >
                  <option value="employee">Employee</option>
                  <option value="department">Department</option>
                  <option value="branch">Branch</option>
                </select>
              </div>

              {/* Dynamic Selection Based on Transfer Type */}
              {transferData.transferType === 'employee' && (
                <div>
                  <Label>Transfer To Employee</Label>
                  {/* Search Input */}
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search employees by name or email..."
                      className="pl-10"
                      value={employeeSearch}
                      onChange={(e) => setEmployeeSearch(e.target.value)}
                    />
                  </div>
                  {/* Employee List */}
                  <div className="mt-2 max-h-48 overflow-y-auto border rounded-md">
                    {(employees || [])
                      .filter((emp: any) => emp.id !== asset.assignedTo?.id)
                      .filter((emp: any) => {
                        if (!employeeSearch) return true;
                        const search = employeeSearch.toLowerCase();
                        return (
                          emp.firstName?.toLowerCase().includes(search) ||
                          emp.lastName?.toLowerCase().includes(search) ||
                          emp.email?.toLowerCase().includes(search) ||
                          emp.employeeId?.toLowerCase().includes(search)
                        );
                      })
                      .slice(0, 50)
                      .map((emp: any) => (
                        <div
                          key={emp.id}
                          className={cn(
                            "px-3 py-2 cursor-pointer hover:bg-muted flex items-center justify-between",
                            transferData.toEmployeeId === emp.id && "bg-primary/10"
                          )}
                          onClick={() => setTransferData({ ...transferData, toEmployeeId: emp.id })}
                        >
                          <div>
                            <p className="text-sm font-medium">{emp.firstName} {emp.lastName}</p>
                            <p className="text-xs text-muted-foreground">{emp.email || emp.employeeId}</p>
                          </div>
                          {transferData.toEmployeeId === emp.id && (
                            <Check className="w-4 h-4 text-primary" />
                          )}
                        </div>
                      ))}
                    {(employees || []).filter((emp: any) => {
                      if (!employeeSearch) return true;
                      const search = employeeSearch.toLowerCase();
                      return (
                        emp.firstName?.toLowerCase().includes(search) ||
                        emp.lastName?.toLowerCase().includes(search) ||
                        emp.email?.toLowerCase().includes(search)
                      );
                    }).length === 0 && (
                      <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                        No employees found
                      </p>
                    )}
                  </div>
                  {transferData.toEmployeeId && (
                    <p className="mt-2 text-sm text-primary">
                      Selected: {(employees || []).find((e: any) => e.id === transferData.toEmployeeId)?.firstName} {(employees || []).find((e: any) => e.id === transferData.toEmployeeId)?.lastName}
                    </p>
                  )}
                </div>
              )}

              {transferData.transferType === 'department' && (
                <div>
                  <Label>Transfer To Department</Label>
                  <select
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    value={transferData.toDepartmentId}
                    onChange={(e) => setTransferData({ ...transferData, toDepartmentId: e.target.value })}
                  >
                    <option value="">Select a department...</option>
                    {(departments || [])
                      .filter((dept: any) => dept.id !== asset.department?.id)
                      .map((dept: any) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {transferData.transferType === 'branch' && (
                <div>
                  <Label>Transfer To Branch</Label>
                  <select
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    value={transferData.toBranchId}
                    onChange={(e) => setTransferData({ ...transferData, toBranchId: e.target.value })}
                  >
                    <option value="">Select a branch...</option>
                    {(branches || [])
                      .filter((b: any) => b.id !== asset.branch?.id)
                      .map((branch: any) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div>
                <Label>Reason (Optional)</Label>
                <Input
                  className="mt-1"
                  placeholder="Enter reason for transfer..."
                  value={transferData.reason}
                  onChange={(e) => setTransferData({ ...transferData, reason: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowTransferModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    const { transferType, ...rest } = transferData;
                    transferMutation.mutate(rest);
                  }}
                  disabled={
                    (transferData.transferType === 'employee' && !transferData.toEmployeeId) ||
                    (transferData.transferType === 'department' && !transferData.toDepartmentId) ||
                    (transferData.transferType === 'branch' && !transferData.toBranchId)
                  }
                  loading={transferMutation.isPending}
                >
                  Transfer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Maintenance Modal */}
      {showMaintenanceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add Maintenance Record</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowMaintenanceModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Type</Label>
                <select
                  className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                  value={maintenanceData.type}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, type: e.target.value })}
                >
                  <option value="PREVENTIVE">Preventive</option>
                  <option value="CORRECTIVE">Corrective</option>
                  <option value="INSPECTION">Inspection</option>
                  <option value="UPGRADE">Upgrade</option>
                  <option value="REPAIR">Repair</option>
                </select>
              </div>
              <div>
                <Label>Description</Label>
                <textarea
                  className="w-full min-h-[80px] px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                  placeholder="Describe the maintenance..."
                  value={maintenanceData.description}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    className="mt-1"
                    value={maintenanceData.startDate}
                    onChange={(e) => setMaintenanceData({ ...maintenanceData, startDate: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Cost (Optional)</Label>
                  <Input
                    type="number"
                    className="mt-1"
                    placeholder="0.00"
                    value={maintenanceData.cost}
                    onChange={(e) => setMaintenanceData({ ...maintenanceData, cost: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowMaintenanceModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => addMaintenanceMutation.mutate(maintenanceData)}
                  disabled={!maintenanceData.description}
                  loading={addMaintenanceMutation.isPending}
                >
                  Add Record
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Send for Repair Modal */}
      {showRepairModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-background z-10">
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Send for Repair
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowRepairModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Asset Info */}
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm font-medium mb-1">Asset</p>
                <p className="text-sm">
                  <span className="font-mono">{asset?.assetTag}</span> - {asset?.name || asset?.model}
                </p>
                {asset?.assignedTo && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Assigned to: {asset.assignedTo.firstName} {asset.assignedTo.lastName}
                  </p>
                )}
              </div>

              {/* Repair Type & Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Repair Type</Label>
                  <select
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    value={repairData.repairType}
                    onChange={(e) => setRepairData({ ...repairData, repairType: e.target.value })}
                  >
                    <option value="CORRECTIVE">Corrective (Broken/Damaged)</option>
                    <option value="PREVENTIVE">Preventive (Scheduled)</option>
                    <option value="WARRANTY">Warranty Claim</option>
                  </select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <select
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    value={repairData.priority}
                    onChange={(e) => setRepairData({ ...repairData, priority: e.target.value })}
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>
              </div>

              {/* Service Provider */}
              <div>
                <Label>Service Provider (Optional)</Label>
                <select
                  className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                  value={repairData.serviceProviderId}
                  onChange={(e) => setRepairData({ ...repairData, serviceProviderId: e.target.value })}
                >
                  <option value="">Select a service provider...</option>
                  {(serviceProviders || []).map((provider: any) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Can be assigned later if not known now
                </p>
              </div>

              {/* Issue Description */}
              <div>
                <Label>Issue Description *</Label>
                <textarea
                  className="w-full min-h-[80px] px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                  placeholder="Describe the problem in detail..."
                  value={repairData.issueDescription}
                  onChange={(e) => setRepairData({ ...repairData, issueDescription: e.target.value })}
                />
              </div>

              {/* Loaner Asset Option */}
              {asset?.assignedTo && (
                <div className="border rounded-lg p-4 bg-blue-50/50">
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      id="assignLoaner"
                      checked={repairData.assignLoaner}
                      onChange={(e) => setRepairData({ 
                        ...repairData, 
                        assignLoaner: e.target.checked,
                        loanerAssetId: e.target.checked ? repairData.loanerAssetId : ''
                      })}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <label htmlFor="assignLoaner" className="text-sm font-medium cursor-pointer">
                      Assign a loaner asset to {asset.assignedTo.firstName} {asset.assignedTo.lastName}
                    </label>
                  </div>
                  
                  {repairData.assignLoaner && (
                    <div className="mt-2">
                      <Label>Select Loaner Asset (Same Category: {asset.category?.name})</Label>
                      {/* Search Input */}
                      <div className="relative mt-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search available assets..."
                          className="pl-10"
                          value={loanerSearch}
                          onChange={(e) => setLoanerSearch(e.target.value)}
                        />
                      </div>
                      {/* Available Assets List */}
                      <div className="mt-2 max-h-32 overflow-y-auto border rounded-md bg-white">
                        {(availableLoanerAssets || [])
                          .filter((a: any) => a.id !== id)
                          .filter((a: any) => {
                            if (!loanerSearch) return true;
                            const search = loanerSearch.toLowerCase();
                            return (
                              a.assetTag?.toLowerCase().includes(search) ||
                              a.name?.toLowerCase().includes(search) ||
                              a.serialNumber?.toLowerCase().includes(search)
                            );
                          })
                          .slice(0, 20)
                          .map((a: any) => (
                            <div
                              key={a.id}
                              className={cn(
                                "px-3 py-2 cursor-pointer hover:bg-muted flex items-center justify-between",
                                repairData.loanerAssetId === a.id && "bg-primary/10"
                              )}
                              onClick={() => setRepairData({ ...repairData, loanerAssetId: a.id })}
                            >
                              <div>
                                <p className="text-sm font-medium font-mono">{a.assetTag}</p>
                                <p className="text-xs text-muted-foreground">
                                  {a.name || a.model} {a.serialNumber && `• S/N: ${a.serialNumber}`}
                                </p>
                              </div>
                              {repairData.loanerAssetId === a.id && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </div>
                          ))}
                        {(availableLoanerAssets || []).filter((a: any) => a.id !== id).length === 0 && (
                          <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                            No available {asset.category?.name} assets for loan
                          </p>
                        )}
                      </div>
                      {repairData.loanerAssetId && (
                        <p className="mt-2 text-sm text-primary">
                          Selected: {(availableLoanerAssets || []).find((a: any) => a.id === repairData.loanerAssetId)?.assetTag}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Estimated Cost & Days */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Estimated Cost (Optional)</Label>
                  <Input
                    type="number"
                    className="mt-1"
                    placeholder="0.00"
                    value={repairData.estimatedCost}
                    onChange={(e) => setRepairData({ ...repairData, estimatedCost: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Estimated Days (Optional)</Label>
                  <Input
                    type="number"
                    className="mt-1"
                    placeholder="e.g. 7"
                    value={repairData.estimatedDays}
                    onChange={(e) => setRepairData({ ...repairData, estimatedDays: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowRepairModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => sendForRepairMutation.mutate(repairData)}
                  disabled={!repairData.issueDescription.trim()}
                  loading={sendForRepairMutation.isPending}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Send for Repair
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-sm mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>QR Code</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowQRModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="flex flex-col items-center space-y-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(JSON.stringify({
                  id: asset.id,
                  assetTag: asset.assetTag,
                  name: asset.name,
                }))}`}
                alt="QR Code"
                className="w-48 h-48"
              />
              <p className="text-sm text-center text-muted-foreground">
                <strong>{asset.assetTag}</strong><br />
                {asset.name}
              </p>
              <Button onClick={generateQRCode}>
                <Download className="w-4 h-4 mr-2" />
                Download QR Code
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/assets">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{asset.name}</h1>
            <p className="text-muted-foreground font-mono">{asset.assetTag}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => setShowQRModal(true)}>
            <QrCode className="w-4 h-4 mr-2" />
            QR Code
          </Button>
          <PermissionGate permission="assets.transfer">
            {/* Show Assign button if AVAILABLE or if ASSIGNED but no employee (branch/dept only) */}
            {(asset.status === 'AVAILABLE' || (asset.status === 'ASSIGNED' && !asset.assignedTo)) ? (
              <Button onClick={() => setShowAssignModal(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Assign
              </Button>
            ) : asset.status === 'ASSIGNED' && asset.assignedTo ? (
              <Button 
                variant="outline" 
                onClick={() => unassignMutation.mutate()}
                loading={unassignMutation.isPending}
              >
                <UserMinus className="w-4 h-4 mr-2" />
                Unassign
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => setShowTransferModal(true)}>
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Transfer
            </Button>
          </PermissionGate>
          <Button 
            variant="outline" 
            onClick={() => setShowReportIssueModal(true)}
            className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
          >
            <MessageSquareWarning className="w-4 h-4 mr-2" />
            Issues
          </Button>
          <PermissionGate permission="assets.repair">
            {asset.status !== 'UNDER_REPAIR' && (
              <Button 
                variant="outline" 
                onClick={() => setShowRepairModal(true)}
                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
              >
                <Wrench className="w-4 h-4 mr-2" />
                Send for Repair
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowMaintenanceModal(true)}>
              <Wrench className="w-4 h-4 mr-2" />
              Maintenance
            </Button>
          </PermissionGate>
          <PermissionGate permission="assets.update">
            <Button variant="outline" asChild>
              <Link to={`/assets/${id}/edit`}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Link>
            </Button>
          </PermissionGate>
          <PermissionGate permission="assets.delete">
            <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Report Issue Dialog */}
      <ReportIssueDialog
        isOpen={showReportIssueModal}
        onClose={() => setShowReportIssueModal(false)}
        assetId={asset.id}
        assetName={asset.name}
        assetTag={asset.assetTag}
        assetCategory={asset.category?.name}
      />

      {/* Status Badge */}
      <div>
        <span className={cn("px-3 py-1.5 text-sm font-medium rounded-full", getStatusColor(asset.status))}>
          {asset.status.replace('_', ' ')}
        </span>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Details */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Asset Details
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Category</p>
              <p className="font-medium">{asset.category?.name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Brand</p>
              <p className="font-medium">{asset.brand?.name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Model</p>
              <p className="font-medium">{asset.model || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Serial Number</p>
              <p className="font-medium font-mono">{asset.serialNumber || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Vendor</p>
              <p className="font-medium">{asset.vendor?.name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Condition</p>
              <p className="font-medium">{asset.condition || '-'}</p>
            </div>
            {asset.description && (
              <div className="sm:col-span-2">
                <p className="text-sm text-muted-foreground">Description</p>
                <p className="font-medium">{asset.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Assignment */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Assignment
              </CardTitle>
            </CardHeader>
            <CardContent>
              {asset.assignedTo ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {asset.assignedTo.firstName?.charAt(0)}{asset.assignedTo.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{asset.assignedTo.firstName} {asset.assignedTo.lastName}</p>
                    <p className="text-sm text-muted-foreground">{asset.assignedTo.email}</p>
                  </div>
                </div>
              ) : asset.branch || asset.department ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {asset.isAssignable === false ? 'Non-assignable asset' : 'Shared/Branch Asset'}
                  </p>
                  {asset.branch && (
                    <p className="font-medium">Branch: {asset.branch.name}</p>
                  )}
                  {asset.department && (
                    <p className="font-medium">Department: {asset.department.name}</p>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">Not assigned to employee, branch, or department</p>
              )}
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <p className="text-sm text-muted-foreground">Branch</p>
                <p className="font-medium">{asset.branch?.name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Department</p>
                <p className="font-medium">{asset.department?.name || '-'}</p>
              </div>
              {asset.location && (
                <div>
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="font-medium">{asset.location.name || '-'}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Financial */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Financial
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <p className="text-sm text-muted-foreground">Purchase Date</p>
                <p className="font-medium">{asset.purchaseDate ? formatDate(asset.purchaseDate) : '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Purchase Cost</p>
                <p className="font-medium">{asset.purchaseCost ? formatCurrency(parseFloat(asset.purchaseCost)) : '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Value</p>
                <p className="font-medium">{asset.currentValue ? formatCurrency(parseFloat(asset.currentValue)) : '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Warranty Expiry</p>
                <p className="font-medium">{asset.warrantyExpiry ? formatDate(asset.warrantyExpiry) : '-'}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* History Tabs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Records
            </CardTitle>
            <div className="flex gap-1">
              <Button
                variant={activeTab === 'history' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('history')}
              >
                History
              </Button>
              <Button
                variant={activeTab === 'maintenance' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('maintenance')}
              >
                Maintenance
              </Button>
              <Button
                variant={activeTab === 'transfers' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('transfers')}
              >
                Transfers
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Assignment History */}
          {activeTab === 'history' && (
            <>
              {(assignmentHistory as HistoryEntry[] || []).length > 0 ? (
                <div className="space-y-4">
                  {(assignmentHistory as HistoryEntry[]).map((entry) => (
                    <div key={entry.id} className="flex gap-4 pb-4 border-b last:border-0">
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                        entry.action === 'ASSIGNED' ? 'bg-green-100 text-green-600' :
                        entry.action === 'UNASSIGNED' ? 'bg-orange-100 text-orange-600' :
                        entry.action === 'TRANSFERRED' ? 'bg-blue-100 text-blue-600' :
                        entry.action === 'BRANCH_CHANGED' ? 'bg-indigo-100 text-indigo-600' :
                        entry.action === 'STATUS_CHANGED' ? 'bg-purple-100 text-purple-600' :
                        entry.action === 'CREATED' ? 'bg-emerald-100 text-emerald-600' :
                        entry.action === 'IMPORTED' ? 'bg-cyan-100 text-cyan-600' :
                        entry.action === 'RETURNED_FROM_REPAIR' ? 'bg-teal-100 text-teal-600' :
                        entry.action === 'RESTORED' ? 'bg-lime-100 text-lime-600' :
                        entry.action === 'UPDATED' ? 'bg-slate-100 text-slate-600' :
                        'bg-muted'
                      )}>
                        {entry.action === 'ASSIGNED' ? <UserPlus className="w-5 h-5" /> :
                         entry.action === 'UNASSIGNED' ? <UserMinus className="w-5 h-5" /> :
                         entry.action === 'TRANSFERRED' ? <ArrowRightLeft className="w-5 h-5" /> :
                         entry.action === 'BRANCH_CHANGED' ? <MapPin className="w-5 h-5" /> :
                         entry.action === 'IMPORTED' ? <Download className="w-5 h-5" /> :
                         entry.action === 'RETURNED_FROM_REPAIR' ? <Wrench className="w-5 h-5" /> :
                         entry.action === 'CREATED' ? <Package className="w-5 h-5" /> :
                         <Clock className="w-5 h-5" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">
                          {entry.action === 'ASSIGNED' && entry.newEmployee ? (
                            <>Assigned to <span className="text-green-600">{entry.newEmployee.firstName} {entry.newEmployee.lastName}</span>
                              {entry.newEmployee.employeeId && <span className="text-muted-foreground text-sm"> ({entry.newEmployee.employeeId})</span>}
                            </>
                          ) : entry.action === 'UNASSIGNED' && entry.oldEmployee ? (
                            <>Unassigned from <span className="text-orange-600">{entry.oldEmployee.firstName} {entry.oldEmployee.lastName}</span>
                              {entry.oldEmployee.employeeId && <span className="text-muted-foreground text-sm"> ({entry.oldEmployee.employeeId})</span>}
                            </>
                          ) : entry.action === 'TRANSFERRED' ? (
                            <>
                              Transferred
                              {entry.oldEmployee && entry.newEmployee && (
                                <> from <span className="text-muted-foreground">{entry.oldEmployee.firstName} {entry.oldEmployee.lastName}</span> to <span className="text-blue-600">{entry.newEmployee.firstName} {entry.newEmployee.lastName}</span></>
                              )}
                              {entry.oldBranch && entry.newBranch && (
                                <> from <span className="text-muted-foreground">{entry.oldBranch.name}</span> to <span className="text-blue-600">{entry.newBranch.name}</span></>
                              )}
                            </>
                          ) : entry.action === 'BRANCH_CHANGED' ? (
                            <>
                              Branch changed
                              {entry.oldBranch && entry.newBranch && (
                                <> from <span className="text-muted-foreground">{entry.oldBranch.name}</span> to <span className="text-indigo-600">{entry.newBranch.name}</span></>
                              )}
                            </>
                          ) : entry.action === 'STATUS_CHANGED' ? (
                            <>Status changed from <span className="text-muted-foreground">{entry.oldStatus?.replace(/_/g, ' ')}</span> to <span className="text-blue-600">{entry.newStatus?.replace(/_/g, ' ')}</span></>
                          ) : entry.action === 'IMPORTED' ? (
                            <span className="text-cyan-600">Imported from external source</span>
                          ) : entry.action === 'CREATED' ? (
                            <span className="text-emerald-600">Asset created</span>
                          ) : entry.action === 'RETURNED_FROM_REPAIR' ? (
                            <span className="text-teal-600">Returned from repair</span>
                          ) : entry.action === 'RESTORED' ? (
                            <span className="text-lime-600">Asset restored</span>
                          ) : entry.action === 'UPDATED' ? (
                            <span className="text-slate-600">Asset details updated</span>
                          ) : (
                            entry.action.replace(/_/g, ' ')
                          )}
                        </p>
                        {entry.notes && <p className="text-sm text-muted-foreground mt-1">{entry.notes}</p>}
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDateTime(entry.performedAt)}
                          {entry.performedByName && ` • by ${entry.performedByName}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No history records yet</p>
                </div>
              )}
            </>
          )}

          {/* Maintenance Records */}
          {activeTab === 'maintenance' && (
            <>
              {(maintenanceRecords as MaintenanceRecord[] || []).length > 0 ? (
                <div className="space-y-4">
                  {(maintenanceRecords as MaintenanceRecord[]).map((record) => (
                    <div key={record.id} className="flex gap-4 pb-4 border-b last:border-0">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <Wrench className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{record.type}</p>
                          <span className={cn(
                            "px-2 py-0.5 text-xs font-medium rounded-full",
                            record.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          )}>
                            {record.status}
                          </span>
                        </div>
                        <p className="text-sm">{record.description}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(record.startDate)}
                          {record.endDate && ` - ${formatDate(record.endDate)}`}
                          {record.cost && ` • Cost: ${formatCurrency(parseFloat(record.cost))}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No maintenance records yet</p>
              )}
            </>
          )}

          {/* Transfer History */}
          {activeTab === 'transfers' && (
            <>
              {(transferHistory as TransferRecord[] || []).length > 0 ? (
                <div className="space-y-4">
                  {(transferHistory as TransferRecord[]).map((transfer) => (
                    <div key={transfer.id} className="flex gap-4 pb-4 border-b last:border-0">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <ArrowRightLeft className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        {/* Employee Transfer */}
                        {(transfer.fromUser || transfer.toUser) && (
                          <p className="font-medium">
                            <span className="text-muted-foreground">Employee:</span>{' '}
                            {transfer.fromUser ? `${transfer.fromUser.firstName} ${transfer.fromUser.lastName}` : 'None'} → {transfer.toUser ? `${transfer.toUser.firstName} ${transfer.toUser.lastName}` : 'None'}
                          </p>
                        )}
                        {/* Department Transfer */}
                        {(transfer.fromDepartment || transfer.toDepartment) && (
                          <p className="font-medium">
                            <span className="text-muted-foreground">Department:</span>{' '}
                            {transfer.fromDepartment?.name || 'None'} → {transfer.toDepartment?.name || 'None'}
                          </p>
                        )}
                        {/* Branch Transfer */}
                        {(transfer.fromBranch || transfer.toBranch) && (
                          <p className="font-medium">
                            <span className="text-muted-foreground">Branch:</span>{' '}
                            {transfer.fromBranch?.name || 'None'} → {transfer.toBranch?.name || 'None'}
                          </p>
                        )}
                        {transfer.reason && <p className="text-sm mt-1">{transfer.reason}</p>}
                        <p className="text-sm text-muted-foreground mt-1">
                          {formatDateTime(transfer.transferDate)}
                          {transfer.transferredBy && ` by ${transfer.transferredBy.firstName} ${transfer.transferredBy.lastName}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No transfer records yet</p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
